// "Request a key": keys are issued by our team, so this files the request where the team works (the shared Sales
// inbox) through the API (GET|POST /v1/account/key-request). The server knows the organisation, plan and paid period;
// the browser sends only its session and an optional note.
import { getAccessToken } from './active-session'

const API_BASE =
  (import.meta.env.VITE_API_BASE as string | undefined) || 'https://api.freightratesmcp.com'

export type KeyRequestState = 'none' | 'sent' | 'in_progress' | 'closed'

export interface KeyRequest {
  state: KeyRequestState
  since: string | null
  /** keys the organisation already has and can use */
  keys: number
}

export type KeyRequestResult =
  | { ok: true; request: KeyRequest }
  | { ok: false; reason: 'signed_out' | 'not_entitled' | 'no_account' | 'unavailable' }

async function call(method: 'GET' | 'POST', note?: string): Promise<KeyRequestResult> {
  try {
    const token = await getAccessToken()
    if (!token) return { ok: false, reason: 'signed_out' }
    const res = await fetch(`${API_BASE}/v1/account/key-request`, {
      method,
      headers: {
        authorization: `Bearer ${token}`,
        ...(method === 'POST' ? { 'content-type': 'application/json' } : {}),
      },
      ...(method === 'POST' ? { body: JSON.stringify(note ? { note } : {}) } : {}),
    })
    if (res.status === 401) return { ok: false, reason: 'signed_out' }
    if (res.status === 402) return { ok: false, reason: 'not_entitled' }
    if (res.status === 409) return { ok: false, reason: 'no_account' }
    if (!res.ok) return { ok: false, reason: 'unavailable' }
    const body = (await res.json()) as { data?: KeyRequest }
    return body.data ? { ok: true, request: body.data } : { ok: false, reason: 'unavailable' }
  } catch {
    return { ok: false, reason: 'unavailable' }
  }
}

export const loadKeyRequest = () => call('GET')
export const requestKey = (note?: string) => call('POST', note)
